// modules/facturacion/components/BusquedaClientesTab.jsx
import React, { useState } from "react";
import {
  Box, Button, TextField, Typography, Alert,
  CircularProgress, IconButton, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon     from "@mui/icons-material/Search";
import ReceiptIcon    from "@mui/icons-material/Receipt";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate } from "react-router-dom";
import axios from "../../../config/axiosConfig";
import PrefacturaServicioModal from "./PrefacturaServicioModal";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
};

const BusquedaClientesTab = () => {
  const navigate = useNavigate();
  const [buscar,    setBuscar]    = useState("");
  const [lista,     setLista]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [buscado,   setBuscado]   = useState(false);
  const [modalData, setModalData] = useState(null);

  const handleBuscar = async () => {
    if (!buscar.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get("/api/facturas/buscar", {
        params: { buscar: buscar.trim() },
      });
      const data = res.data?.data ?? res.data;
      setLista(Array.isArray(data) ? data : []);
      setBuscado(true);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al buscar");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleBuscar();
  };

  const handleSuccess = (codFactura) => {
    const codServicio = modalData?.servicio?.cod_servicio_cliente;
    setModalData(null);

    if (codFactura) {
      navigate(`/facturacion/facturas/${codFactura}`, {
        state: {
          from: "/facturacion",
          cod_servicio_cliente: codServicio,
        },
      });
    } else if (codServicio) {
      navigate(`/reporte-cliente/${codServicio}`);
    }
  };

  return (
    <Box>
      {/* ── Búsqueda ── */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
        <TextField
          size="small"
          label="Buscar por Razón Social"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ ...compactFieldSx, width: { xs: "100%", sm: 260 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleBuscar} disabled={loading}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={handleBuscar}
          disabled={loading || !buscar.trim()}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
          sx={{ minHeight: 32 }}
        >
          Buscar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Resultados ── */}
      {buscado && (
        lista.length === 0 ? (
          <Alert severity="info">No se encontraron servicios para "{buscar}".</Alert>
        ) : (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.25, px: 0.75, fontSize: "0.78rem" } }}>
                <TableHead>
                  <TableRow>
                    <StyledHeaderCell>COD Servicio</StyledHeaderCell>
                    <StyledHeaderCell>Razón Social</StyledHeaderCell>
                    <StyledHeaderCell>Grupo</StyledHeaderCell>
                    <StyledHeaderCell>Nombre servicio</StyledHeaderCell>
                    <StyledHeaderCell>Servicio</StyledHeaderCell>
                    <StyledHeaderCell>Tipo</StyledHeaderCell>
                    <StyledHeaderCell>Sucursal</StyledHeaderCell>
                    <StyledHeaderCell>Descripción</StyledHeaderCell>
                    <StyledHeaderCell align="center">Acción</StyledHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lista.map((s) => (
                    <TableRow key={s.cod_servicio_cliente} hover>
                      <TableCell>{s.cod_servicio_cliente}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {s.razon_social}
                        </Typography>
                      </TableCell>
                      <TableCell>{s.grupo || "-"}</TableCell>
                      <TableCell>{s.cliente_servicio_nombre || "-"}</TableCell>
                      <TableCell>{s.servicio_nombre}</TableCell>
                      <TableCell>{s.tipo}</TableCell>
                      <TableCell>{s.sucursal_facturar}</TableCell>
                      <TableCell>{s.descripcion}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          <Button size="small" variant="outlined" color="warning"
                            startIcon={<DescriptionIcon />}
                            sx={{ minHeight: 28, fontSize: "0.72rem", px: 0.75 }}
                            onClick={() => setModalData({ servicio: s, tipo: "pre" })}>
                            Pre-Factura
                          </Button>
                          <Button size="small" variant="outlined" color="primary"
                            startIcon={<ReceiptIcon />}
                            sx={{ minHeight: 28, fontSize: "0.72rem", px: 0.75 }}
                            onClick={() => setModalData({ servicio: s, tipo: "fac" })}>
                            Factura
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      )}

      {modalData && (
        <PrefacturaServicioModal
          open={!!modalData}
          onClose={() => setModalData(null)}
          onSuccess={handleSuccess}
          servicio={modalData.servicio}
          tipo={modalData.tipo}
          individual={true}
        />
      )}
    </Box>
  );
};

export default BusquedaClientesTab;

